<template>
  <div>
    <v-card>
      <v-card-text>
        <h2><v-icon>lock</v-icon>Security</h2>
        <v-btn @click="generatePassword" v-if="allowed && !entity.userz"
          >Generate username and password</v-btn
        >
        <v-switch v-model="allowed" label="Login access" @change="updateDeny" />
        <template v-if="entity.loginDetails && entity.loginDetails.loginUser">
          <template v-if="!entity.loginDetails.deny">
            <table>
              <tr>
                <td>
                  <strong>Username</strong>
                </td>
                <td>
                  <a
                    v-if="entity.loginDetails.primary"
                    :href="`mailto:${entity.loginDetails.loginUser.username}`"
                    >{{ entity.loginDetails.loginUser.username }}</a
                  >
                  <template v-if="!entity.loginDetails.primary">
                    <v-text-field
                      v-model="username"
                      @input="usernameDebounce"
                      v-if="$keycloak.hasRealmRole('root')"
                    />
                    <span v-if="!$keycloak.hasRealmRole('root')">{{
                      entity.loginDetails.loginUser.username
                    }}</span>
                  </template>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        @click="removePrimary"
                        v-if="entity.loginDetails.primary"
                        color="error"
                        small
                        v-on="on"
                      >
                        <v-icon>delete</v-icon>Primary email
                      </v-btn>
                    </template>
                    <span>Remove primary email address from this account</span>
                  </v-tooltip>
                </td>
              </tr>
              <tr v-if="$keycloak.hasRealmRole('root')">
                <td>
                  <strong>Password</strong>
                </td>
                <td>
                  {{ entity.loginDetails.loginUser.password }}
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn @click="generatePassword" small v-on="on"
                        >Generate new password</v-btn
                      >
                    </template>
                    <span>Generate a new password</span>
                  </v-tooltip>
                </td>
              </tr>
            </table>
            <v-switch
              v-model="optin"
              label="Email notifications"
              @change="updateOptout"
              v-if="entity.loginDetails.primary"
            />
          </template>
        </template>
        <template v-if="entity.typeid == 3 || entity.typeid == 4">
          <span v-if="entity.gdpr">
            <strong>Gdpr agreed</strong>
            {{ entity.gdpr.agreed }}
          </span>
          <v-alert :value="!entity.gdpr" type="error"
            >GDPR object not initialised</v-alert
          >
        </template>
      </v-card-text>
    </v-card>
    <v-card v-if="entity.userz">
      <v-card-text>
        <h2>OpenID Connect</h2>
        <a
          :href="`${keycloakFile['auth-server-url']}/realms/homeconvey-production/account`"
          target="_blank"
          v-if="$keycloak.tokenParsed.entityid === entity.id"
          >Go to keycloak account page</a
        >
        <v-btn
          v-if="!entity.userz.userRepresentation"
          @click="createUserRepresentation"
          >Create user representation</v-btn
        >
        <template v-if="entity.userz.userRepresentation">
          <div
            v-if="
              !entity.openid ||
              entity.openid !== entity.userz.userRepresentation.id
            "
          >
            Openid not linked to record!
            <v-btn color="error" @click="updateOpenid()">Reset openid</v-btn>
          </div>
          <div v-if="entity.openid">
            <v-text-field
              label="Id"
              :value="entity.userz.userRepresentation.id"
              readonly
            />
            <v-data-table
              :headers="headers"
              :items="entity.federatedIdentityRepresentations"
              v-if="entity.federatedIdentityRepresentations"
              hide-actions
              class="elevation-1"
            >
              <template v-slot:items="props">
                <tr>
                  <td>{{ props.item.identityProvider }}</td>
                  <td>{{ props.item.userId }}</td>
                  <td>{{ props.item.userName }}</td>
                  <td>
                    <a :href="`https://facebook.com/${props.item.userId}`"
                      >facebook</a
                    >
                    <a :href="`https://plus.google.com/${props.item.userId}`"
                      >google</a
                    >
                    <a
                      :href="`https://www.linkedin.com/in/${props.item.userName}`"
                      >linkedin</a
                    >
                  </td>
                </tr>
              </template>
            </v-data-table>
            <v-alert
              :value="!entity.federatedIdentityRepresentations"
              type="warning"
              >No federated identity representations</v-alert
            >
            <v-text-field
              label="Verification token status"
              :value="entity.verificationTokenStatus"
              v-if="entity.verificationTokenStatus"
              readonly
            />
            <v-text-field
              label="Verification token sent"
              :value="entity.verificationTokenSent"
              v-if="entity.verificationTokenSent"
              readonly
            />
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn @click="executeActionsEmail" color="info" v-on="on">
                  <v-icon>send</v-icon>Verification
                </v-btn>
              </template>
              <span>Send verification email</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn @click="updateRoles" v-on="on">
                  <v-icon>refresh</v-icon>Roles
                </v-btn>
              </template>
              <span>Update user roles</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn @click="resetPassword" v-on="on">Reset password</v-btn>
              </template>
              <span>Set a password on OpenID</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  @click="removeUserRepresentation"
                  color="error"
                  v-on="on"
                >
                  <v-icon>delete</v-icon>Delete
                </v-btn>
              </template>
              <span>Delete user representation</span>
            </v-tooltip>
            <div>Openid: {{ entity.openid }}</div>
            <div>
              <span v-if="entity.userz">{{
                `User, username: ${entity.userz.username}, password: ${entity.userz.password}, version: ${entity.userz.version}`
              }}</span>
            </div>
            <v-alert :value="!entity.userz" type="warning">No user</v-alert>
            <div
              v-for="(value, propertyName) in entity.userz.userRepresentation
                .attributes"
              :key="propertyName"
            >
              {{ `attribute: ${propertyName} ${value}` }}
            </div>
          </div>
        </template>
      </v-card-text>
    </v-card>
    <v-snackbar
      v-model="snackbar"
      :multi-line="true"
      :timeout="3000"
      :top="true"
    >
      {{ snackbarText }}
      <v-btn color="pink" flat @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import CREATE_ENTITY_USER_REPRESENTATION from "@/graphql/directory/CreateEntityUserRepresentation";
import DELETE_ENTITY_PRIMARY from "@/graphql/directory/DeleteEntityPrimary";
import DELETE_ENTITY_USER_REPRESENTATION from "@/graphql/directory/DeleteEntityUserRepresentation";
import UPDATE_ENTITY_DENY from "@/graphql/directory/UpdateEntityDeny";
import UPDATE_ENTITY_EXECUTE_ACTIONS_EMAIL from "@/graphql/directory/UpdateEntityExecuteActionsEmail";
import UPDATE_ENTITY_OPENID from "@/graphql/directory/UpdateEntityOpenid";
import UPDATE_ENTITY_OPTOUT from "@/graphql/directory/UpdateEntityOptout";
import UPDATE_ENTITY_PASSWORD from "@/graphql/directory/UpdateEntityPassword";
import UPDATE_ENTITY_PASSWORD_KEYCLOAK from "@/graphql/directory/UpdateEntityPasswordKeycloak";
import UPDATE_ENTITY_USER_ROLES_KEYCLOAK from "@/graphql/directory/UpdateEntityUserRolesKeycloak";
import UPDATE_ENTITY_USERNAME from "@/graphql/directory/UpdateEntityUsername";
function initialState() {
  return {
    allowed: null,
    optin: false,
    accessDenied: true,
    keycloakFile: null,
    username: null,
    headers: [
      {
        text: "Identity provider",
        value: "identityProvider"
      },
      {
        text: "User id",
        value: "userId"
      },
      {
        text: "User name",
        value: "userName"
      },
      {
        text: "Links",
        value: "userId"
      }
    ],
    snackbar: false,
    snackbarText: null,
    timerId: null
  };
}
export default {
  name: "Security",
  props: ["entity", "lastUpdated"],
  data: function () {
    return initialState();
  },
  methods: {
    async generatePassword() {
      await this.$apollo.mutate({
        mutation: UPDATE_ENTITY_PASSWORD,
        variables: {
          id: Number(this.$route.params.id)
        }
      });
      // console.log(response.data);
      this.$emit("triggerUpdate");
      this.snackbar = true;
      this.snackbarText = "New password has been generated";
    },
    async removePrimary() {
      if (confirm("Are you sure?")) {
        await this.deleteUserRepresentation();
        await this.deletePrimary();
        this.snackbar = true;
        this.snackbarText = "Primary email address removed";
        this.$emit("triggerUpdate");
      }
    },
    async deletePrimary() {
      await this.$apollo.mutate({
        mutation: DELETE_ENTITY_PRIMARY,
        variables: {
          id: Number(this.$route.params.id)
        }
      });
    },
    async updateDeny() {
      await this.$apollo.mutate({
        mutation: UPDATE_ENTITY_DENY,
        variables: {
          id: Number(this.$route.params.id),
          value: !this.allowed
        },
        error(err) {
          console.error(err);
        }
      });
      // console.log(response.data);
      this.$emit("triggerUpdate");
      this.snackbar = true;
      this.snackbarText = "Access has been changed";
    },
    async updateOptout() {
      await this.$apollo.mutate({
        mutation: UPDATE_ENTITY_OPTOUT,
        variables: {
          id: Number(this.$route.params.id),
          value: !this.optin
        },
        error(err) {
          console.error(err);
        }
      });
      // console.log(response.data);
      this.$emit("triggerUpdate");
      this.snackbar = true;
      this.snackbarText = "Email notifications changed";
    },
    async createUserRepresentation() {
      await this.$apollo.mutate({
        mutation: CREATE_ENTITY_USER_REPRESENTATION,
        variables: {
          id: Number(this.$route.params.id)
        },
        error(err) {
          console.error(err);
          this.snackbar = true;
          this.snackbarText = "Failed to create";
        }
      });
      // console.log(response.data);
      this.$emit("triggerUpdate");
    },
    async removeUserRepresentation() {
      if (confirm("Are you sure?")) {
        await this.deleteUserRepresentation();
        this.$emit("triggerUpdate");
        this.snackbar = true;
        this.snackbarText = "Deleted OpenID account";
      }
    },
    async deleteUserRepresentation() {
      await this.$apollo.mutate({
        mutation: DELETE_ENTITY_USER_REPRESENTATION,
        variables: {
          id: Number(this.$route.params.id)
        },
        error(err) {
          console.error(err);
        }
      });
    },
    async executeActionsEmail() {
      await this.$apollo.mutate({
        mutation: UPDATE_ENTITY_EXECUTE_ACTIONS_EMAIL,
        variables: {
          id: Number(this.$route.params.id)
        }
      });
      // console.log(response.data);
      this.$emit("triggerUpdate");
      this.snackbar = true;
      this.snackbarText = "Sent email";
    },
    async updateRoles() {
      await this.$apollo.mutate({
        mutation: UPDATE_ENTITY_USER_ROLES_KEYCLOAK,
        variables: {
          id: Number(this.$route.params.id)
        }
      });
      // console.log(response.data);
      this.$emit("triggerUpdate");
      this.snackbar = true;
      this.snackbarText = "Updated user roles for Open id";
    },
    async resetPassword() {
      await this.$apollo.mutate({
        mutation: UPDATE_ENTITY_PASSWORD_KEYCLOAK,
        variables: {
          id: Number(this.$route.params.id)
        }
      });
      // console.log(response.data);
      this.$emit("triggerUpdate");
      this.snackbar = true;
      this.snackbarText = `Reset password`;
    },
    async updateOpenid() {
      await this.$apollo.mutate({
        mutation: UPDATE_ENTITY_OPENID,
        variables: {
          id: Number(this.$route.params.id)
        },
        error(err) {
          console.error(err);
        }
      });
      // console.log(response.data);
      this.$emit("triggerUpdate");
    },
    async updateUsername() {
      await this.$apollo.mutate({
        mutation: UPDATE_ENTITY_USERNAME,
        variables: {
          id: Number(this.$route.params.id),
          username: this.username
        }
      });
      // console.log(response.data);
      this.$emit("triggerUpdate");
    },
    usernameDebounce() {
      clearTimeout(this.timerId);
      this.timerId = setTimeout(() => {
        this.updateUsername();
      }, 500);
    }
  },
  watch: {
    entity: {
      immediate: true,
      handler() {
        this.allowed = this.entity.loginDetails
          ? !this.entity.loginDetails.deny
          : null;
        this.optin = this.entity.loginDetails
          ? !this.entity.loginDetails.optout
          : false;
        this.username = this.entity.loginDetails
          ? this.entity.loginDetails.loginUser.username
          : null;
      }
    }
  },
  async mounted() {
    const response = await fetch(`/${process.env.VUE_APP_KC_PATH}`);
    this.keycloakFile = await response.json();
  }
};
</script>
